/**
 * CategoryFilter - Filter entries by category and type with checkboxes
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Filter,
  ChevronDown,
  ChevronUp,
  Check,
  Briefcase,
  Home,
  CreditCard,
  Landmark,
  Tv,
  Receipt,
  ArrowRightLeft,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Utensils,
  Car,
  Heart,
  Gamepad2,
  MoreHorizontal,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

// Category icons and labels
const CATEGORY_CONFIG = {
  salary: { icon: Briefcase, label: 'Maaş' },
  rent: { icon: Home, label: 'Kira' },
  credit_card: { icon: CreditCard, label: 'Kredi Kartı' },
  loan: { icon: Landmark, label: 'Kredi/Taksit' },
  subscription: { icon: Tv, label: 'Abonelik' },
  bill: { icon: Receipt, label: 'Fatura' },
  transfer: { icon: ArrowRightLeft, label: 'Transfer' },
  investment: { icon: TrendingUp, label: 'Yatırım' },
  shopping: { icon: ShoppingBag, label: 'Alışveriş' },
  food: { icon: Utensils, label: 'Yemek' },
  transport: { icon: Car, label: 'Ulaşım' },
  health: { icon: Heart, label: 'Sağlık' },
  entertainment: { icon: Gamepad2, label: 'Eğlence' },
  other: { icon: MoreHorizontal, label: 'Diğer' },
};

const TYPE_CONFIG = {
  income: { label: 'Gelir', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' },
  expense: { label: 'Gider', color: 'text-red-400', bgColor: 'bg-red-500/20' },
};

const CategoryFilter = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    availableFilters,
    visibleFilters,
    toggleFilter,
    selectAllFilters,
    deselectAllFilters,
  } = useCashflowStore();

  if (!availableFilters || availableFilters.length === 0) {
    return null;
  }

  const visibleCount = visibleFilters.size;
  const totalCount = availableFilters.length;

  // Group filters by type for better display
  const incomeFilters = availableFilters.filter(f => f.endsWith(':income'));
  const expenseFilters = availableFilters.filter(f => f.endsWith(':expense'));

  return (
    <div className="relative">
      {/* Toggle button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
          visibleCount < totalCount
            ? 'bg-indigo-900/30 border-indigo-500/50 text-indigo-400'
            : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
        }`}
      >
        <Filter className="w-4 h-4" />
        <span className="text-sm">
          {visibleCount < totalCount ? `${visibleCount}/${totalCount}` : 'Filtre'}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-2 z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl min-w-[280px] overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">Filtreler</span>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllFilters}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    Tümü
                  </button>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={deselectAllFilters}
                    className="text-xs text-gray-400 hover:text-gray-300"
                  >
                    Hiçbiri
                  </button>
                </div>
              </div>
            </div>

            {/* Filter list */}
            <div className="max-h-80 overflow-y-auto">
              {/* Income section */}
              {incomeFilters.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-emerald-900/20 border-b border-gray-800">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs font-medium text-emerald-400">Gelirler</span>
                    </div>
                  </div>
                  {incomeFilters.map((filterKey) => {
                    const [category] = filterKey.split(':');
                    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
                    const Icon = config.icon;
                    const isVisible = visibleFilters.has(filterKey);

                    return (
                      <button
                        key={filterKey}
                        onClick={() => toggleFilter(filterKey)}
                        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-800/50 transition-colors"
                      >
                        {/* Checkbox */}
                        <div
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            isVisible
                              ? 'bg-emerald-500 border-emerald-500'
                              : 'border-gray-600'
                          }`}
                        >
                          {isVisible && <Check className="w-3 h-3 text-white" />}
                        </div>

                        {/* Icon */}
                        <Icon className={`w-4 h-4 ${isVisible ? 'text-gray-300' : 'text-gray-600'}`} />

                        {/* Label */}
                        <span className={`text-sm ${isVisible ? 'text-gray-200' : 'text-gray-500'}`}>
                          {config.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Expense section */}
              {expenseFilters.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-red-900/20 border-y border-gray-800">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-400" />
                      <span className="text-xs font-medium text-red-400">Giderler</span>
                    </div>
                  </div>
                  {expenseFilters.map((filterKey) => {
                    const [category] = filterKey.split(':');
                    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
                    const Icon = config.icon;
                    const isVisible = visibleFilters.has(filterKey);

                    return (
                      <button
                        key={filterKey}
                        onClick={() => toggleFilter(filterKey)}
                        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-800/50 transition-colors"
                      >
                        {/* Checkbox */}
                        <div
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            isVisible
                              ? 'bg-red-500 border-red-500'
                              : 'border-gray-600'
                          }`}
                        >
                          {isVisible && <Check className="w-3 h-3 text-white" />}
                        </div>

                        {/* Icon */}
                        <Icon className={`w-4 h-4 ${isVisible ? 'text-gray-300' : 'text-gray-600'}`} />

                        {/* Label */}
                        <span className={`text-sm ${isVisible ? 'text-gray-200' : 'text-gray-500'}`}>
                          {config.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer info */}
            {visibleCount < totalCount && (
              <div className="px-4 py-2 border-t border-gray-800 bg-gray-800/30">
                <p className="text-xs text-gray-500">
                  {totalCount - visibleCount} filtre gizlendi
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default CategoryFilter;
